import type { Cart, CartItem } from './CartStorageService'

import { apiUrl } from '../Utility/ApiUrl'

export interface AddItemInput {
  courseDateId?: string
  courseId: string
  courseVariantId?: string
  quantity?: number
}

interface RawCart {
  cartId: null | string
  expiresAt: null | string
  items: RawCartItem[]
  remainingSeconds: number
  totalAmount: string
}

interface RawCartItem {
  availableExtras?: RawCartOptionGroup[]
  course: { format: string, id: string, memberPrice: string, memberPriceGross: null | string, price: string, priceGross: null | string, title: string, vaNumber: null | string }
  courseDate: { endDate: null | string, id: null | string, isDateRange: boolean, location: null | RawCartLocation, startDate: null | string, timeRange: null | string } | null
  courseVariant: { format: string, id: string } | null
  isMemberPrice: boolean
  isModule?: boolean
  itemId: string
  quantity: number
  series?: { id: string, moduleCount: null | number, modulePosition: null | number, title: string } | null
  unitPrice: string
}

interface RawCartLocation {
  city: string
  name: string
  postalCode: string
  street: string
}

interface RawCartOptionChoice {
  id: string
  title: string
}

interface RawCartOptionGroup {
  choices: RawCartOptionChoice[]
  id: string
  isMandatory: boolean
  isSingleChoice: boolean
  title: string
}

// Controller errors carry { error, message }; the routing middleware emits RFC 7807
// problem details { type, title, status, detail } — accept both. When the backend supplies
// no readable text, message stays empty and the display layer falls back to a localised
// generic message (see CartService).
interface RawError {
  detail?: string
  error?: string
  message?: string
  title?: string
}

export class CartError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

const toCartError = (raw: RawError): CartError =>
  new CartError(raw.error ?? 'error', raw.message ?? raw.detail ?? raw.title ?? '')

const toCart = (raw: RawCart): Cart => ({
  cartId: raw.cartId,
  expiresAt: raw.expiresAt === null ? 0 : new Date(raw.expiresAt).getTime(),
  items: raw.items.map((item: RawCartItem): CartItem => ({
    ...item,
    availableExtras: item.availableExtras ?? [],
    isModule: item.isModule ?? false,
    series: item.series ?? null,
  })),
  remainingSeconds: raw.remainingSeconds,
  totalAmount: raw.totalAmount,
})

class CartApiService {
  async addItem(input: AddItemInput): Promise<Cart> {
    return this.request('POST', '/api/cart/items', input)
  }

  async extendReservation(): Promise<Cart | null> {
    try {
      return await this.request('POST', '/api/cart/refresh')
    } catch (error) {
      if (error instanceof CartError && error.code === 'cart_expired') return null
      throw error
    }
  }

  async getCart(): Promise<Cart> {
    return this.request('GET', '/api/cart')
  }

  async removeItem(itemId: string): Promise<Cart> {
    return this.request('DELETE', `/api/cart/items/${encodeURIComponent(itemId)}`)
  }

  async updateItem(itemId: string, quantity: number): Promise<Cart> {
    return this.request('PATCH', `/api/cart/items/${encodeURIComponent(itemId)}?quantity=${quantity.toString()}`)
  }

  private async request(method: string, url: string, body?: unknown): Promise<Cart> {
    const response = await fetch(apiUrl(url), {
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
      method,
    })

    const data = (await response.json()) as RawCart | RawError

    if (response.ok === false) {
      throw toCartError(data as RawError)
    }

    return toCart(data as RawCart)
  }
}

export default new CartApiService()
